/**
 * AQ-5 Cart-stage SMS opt-in handler.
 * Wires the aq5-cart-optin.liquid snippet to:
 *   1. Validate US phone input (formatted on-the-fly as user types)
 *   2. Visual-only enable/disable of submit (no `disabled` attr — D-030)
 *   3. On submit:
 *        - POST /cart/add.js for the chosen free gift variant (D-027: no app)
 *        - POST phone to Klaviyo via /client/subscriptions/ (revision 2026-04-15)
 *          — AQ5-4 (2026-07-07): gift add runs FIRST; subscribe only fires once
 *          the gift is actually in the cart (no false "gift added" welcome SMS)
 *        - POST /cart/update.js to set _aq5_optin/_aq5_giftchoice/_aq5_phone attrs
 *        - Dispatch cart:refresh + cart:updated events; reload on /cart
 *
 * Q9 (D-026) — Cart-aware gift exclusion handled in Liquid (server-side).
 * Q10 (D-026) + D-031 — Soft 20-min urgency timer: motivational only, NEVER
 *   blocks the form. localStorage 'aq5_offer_expires_at' with 24h stale-guard.
 *   On expiry: timer text disappears; form remains fully functional.
 * Q11 (D-028) — Single-step TCPA consent. No standalone checkbox; submit click
 *   = affirmative consent (disclosure rendered directly above the button).
 * Q12 (D-029) — "Already opted in" check uses CART LINE ITEMS (not attribute),
 *   since attributes persist across cart-clear in Shopify.
 *
 * Loading: this script is loaded from layout/theme.liquid on every page (D-033)
 * via the deploy script's AQ5_CONFIG_BLOCK. The IIFE guard window.__aq5_initialized
 * prevents double-init.
 *
 * Klaviyo wiring: primary path is the public Subscriptions API. Secondary path
 * is the storefront-global klaviyo.push(['identify',...]) for browsing-session
 * attribution (fires alongside the API call; does not subscribe to the list).
 */
(function () {
  'use strict';

  // The snippet renders the <script> tag inside its markup. On /cart, the
  // snippet renders twice (cart-template + cart-drawer) → IIFE could execute
  // twice → duplicate listeners. Guard with a window-level flag.
  if (window.__aq5_initialized) return;
  window.__aq5_initialized = true;

  // ============================================================
  // CONFIG — Trevor: replace with your Klaviyo list ID after T1
  // ============================================================
  const KLAVIYO_LIST_ID = window.AQ5_KLAVIYO_LIST_ID || 'TODO_KLAVIYO_LIST_ID';
  const KLAVIYO_COMPANY_ID = window.AQ5_KLAVIYO_COMPANY_ID || 'TODO_KLAVIYO_PUBLIC_KEY';
  // ============================================================

  const US_PHONE_RE = /^\+?1?\s*\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const TIMER_LS_KEY = 'aq5_offer_expires_at';
  const STALE_GUARD_MS = 24 * 60 * 60 * 1000; // 24h: ignore very-old localStorage entries
  // AQ5-2 (2026-07-07): sessionStorage flag set when the Klaviyo subscribe is
  // blocked at network level (ad-blocker). Survives the /cart location.reload()
  // so hydrate() can append the honest note to the confirmed state.
  const SMS_BLOCKED_SS_KEY = 'aq5_sms_blocked';

  // D-245 (2026-10-09, A12): Canadian area codes are not valid here (US-only texts; same list as the quiz popup).
  // They fall through to the existing "10-digit US phone number" message: no new copy, nothing changes for US numbers.
  const CA_NPA = '204 226 236 249 250 263 289 306 343 354 365 367 368 382 387 403 416 418 428 431 437 438 450 460 468 474 506 514 519 548 579 581 584 587 600 604 613 639 647 672 683 705 709 742 753 778 780 782 807 819 825 867 873 879 902 905 942';
  function normalizePhone(raw) {
    const digits = raw.replace(/\D/g, '');
    const ten = digits.length === 10 ? digits : (digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : null);
    if (!ten || CA_NPA.indexOf(ten.slice(0, 3)) !== -1) return null;
    return '+1' + ten;
  }

  // Format as user types: 5551234567 -> (555) 123-4567
  // Strips non-digits + reformats progressively. Acceptable cursor behavior:
  // typing forward keeps cursor at end (standard UX for phone inputs).
  // D-037 (2026-05-27): strip leading 1 (US country code) so autofill formats
  // like +15551234567 don't get truncated. Paste/autofill of contacts is a
  // major mobile user path and previously corrupted the area code by eating
  // the leading 1 as the first digit + dropping the last digit via slice(0,10).
  function formatPhoneAsTyped(raw) {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return '(' + digits;
    if (digits.length <= 6) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
    return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
  }

  function setError(root, msg) {
    const el = root.querySelector('[data-aq5-error]');
    if (el) el.textContent = msg || '';
  }

  function gtmPush(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload || {}));
    } catch (e) { /* swallow */ }
  }

  // D-038 (2026-05-27): wrap fetch with AbortController + timeout. Without this,
  // a hanging /cart.js or Klaviyo request leaves the user stuck in "Adding…"
  // with no recovery. AbortError surfaces in catch blocks; callers handle it.
  function fetchWithTimeout(url, options, timeoutMs) {
    timeoutMs = timeoutMs || 10000;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    const merged = Object.assign({}, options || {}, { signal: controller.signal });
    return fetch(url, merged).finally(() => clearTimeout(id));
  }

  // D-046 (iter 2): fetch + body-read in a single timeout scope. Plain
  // fetchWithTimeout only protects the header phase — the .then(r => r.json())
  // body-read is unbounded. Use this helper anywhere you read the body.
  // Returns { res, json } so callers can still inspect status / headers.
  async function fetchJsonWithTimeout(url, options, timeoutMs) {
    timeoutMs = timeoutMs || 15000;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const merged = Object.assign({}, options || {}, { signal: controller.signal });
      const res = await fetch(url, merged);
      let json = null;
      try { json = await res.json(); } catch (e) { json = null; }
      return { res, json };
    } finally {
      clearTimeout(id);
    }
  }

  // ---------- Urgency timer (Q10) ----------
  function readExpiry() {
    try {
      const raw = localStorage.getItem(TIMER_LS_KEY);
      if (!raw) return null;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n)) return null;
      // 24h stale guard — treat as fresh
      if (Date.now() - n > STALE_GUARD_MS) return null;
      // Upper bound: a corrupted/clock-skewed far-future value would otherwise
      // be trusted forever ("Reserved for 2879:12"). Max legit horizon is the
      // 20-min duration + 1 min slack; reject anything beyond as if absent.
      if (n - Date.now() > 21 * 60 * 1000) return null;
      return n;
    } catch (e) { return null; }
  }

  function writeExpiry(ts) {
    try { localStorage.setItem(TIMER_LS_KEY, String(ts)); } catch (e) { /* swallow */ }
  }

  function clearExpiry() {
    try { localStorage.removeItem(TIMER_LS_KEY); } catch (e) { /* swallow */ }
  }

  function getOrInitExpiry(durationMinutes) {
    const existing = readExpiry();
    if (existing) return existing;
    const fresh = Date.now() + durationMinutes * 60 * 1000;
    writeExpiry(fresh);
    return fresh;
  }

  function fmtMMSS(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // D-031 (2026-05-27): soft urgency — timer is motivational only, NEVER blocks
  // submission. When countdown hits 0, we just hide the timer text. The form
  // remains fully functional. This removes the brittle "expired state" UI that
  // could trap users (especially from a prior session's stale localStorage).
  function startTimer(root) {
    const timerEl = root.querySelector('[data-aq5-timer]');
    if (!timerEl) return;

    // Confirmed state (timer rendered but no [data-aq5-active] fieldset) is
    // READ-ONLY: never initialize a fresh expiry there. Otherwise the submit
    // flow's clearExpiry() + re-render would mint a brand-new now+20min timer,
    // restarting the countdown at 20:00 for every converting user (and giving
    // returning confirmed customers phantom timers). If nothing is stored,
    // blank the element and show no countdown.
    const isActive = !!root.querySelector('[data-aq5-active]');
    const durationMin = parseInt(root.getAttribute('data-aq5-duration-min'), 10) || 20;
    const expiresAt = isActive ? getOrInitExpiry(durationMin) : readExpiry();
    if (!expiresAt) {
      timerEl.textContent = '';
      return;
    }

    function tick() {
      // Self-clear if the DOM node is no longer attached (Maestrostack innerHTML
      // replacement detaches the old node; the interval would otherwise tick
      // forever on a GC-eligible orphan).
      if (!document.contains(timerEl)) {
        return false;
      }
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        timerEl.textContent = '';
        return false; // stop the interval; no other side effects
      }
      timerEl.textContent = `Reserved for ${fmtMMSS(remaining)}`;
      return true;
    }

    if (!tick()) return; // expired — just don't show the countdown
    const iv = setInterval(() => {
      if (!tick()) {
        clearInterval(iv);
        root.__aq5TimerInterval = null;
      }
    }, 1000);
    root.__aq5TimerInterval = iv;
  }
  // ----------------------------------------

  // D-039 (2026-05-27): pass giftChoice as a profile property so the Klaviyo
  // welcome flow can personalize the SMS ("Your free scrunchie is added!" /
  // "Your free claw clip is added!"). Trevor's welcome flow template should
  // reference {{ person.properties.aq5_giftchoice }}.
  async function subscribeKlaviyo(phone, email, giftChoice) {
    // Secondary: storefront-global identify. Only updates profile props + attaches
    // the phone-keyed profile to current browsing-session attribution. Does NOT
    // subscribe to a specific list (that's why we ALSO hit the public API below).
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', {
          '$phone_number': phone,
          ...(email ? { '$email': email } : {}),
          ...(giftChoice ? {
            'aq5_giftchoice': giftChoice,
            'aq5_optin_at': new Date().toISOString(),
          } : {}),
        }]);
      }
    } catch (e) { /* secondary path — swallow */ }

    // Primary: public Subscriptions API. Adds to the AQ-5 list, captures SMS
    // consent (TCPA-compliant via custom_source attribution), triggers the
    // "Added to list" welcome flow.
    if (KLAVIYO_COMPANY_ID === 'TODO_KLAVIYO_PUBLIC_KEY' || KLAVIYO_LIST_ID === 'TODO_KLAVIYO_LIST_ID') {
      console.warn('[AQ5] Klaviyo config not set — opt-in not delivered. Set window.AQ5_KLAVIYO_LIST_ID + window.AQ5_KLAVIYO_COMPANY_ID.');
      return { ok: false, via: 'unconfigured' };
    }
    const profileAttrs = {
      phone_number: phone,
      ...(email ? { email: email } : {}),
      ...(giftChoice ? {
        properties: {
          aq5_giftchoice: giftChoice,
          aq5_optin_at: new Date().toISOString(),
        },
      } : {}),
    };
    const kdirect = async () => {
    try {
      const res = await fetchWithTimeout(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(KLAVIYO_COMPANY_ID)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          'revision': '2026-04-15',
        },
        body: JSON.stringify({
          // Schema per Klaviyo Create Client Subscription endpoint, revision 2026-04-15.
          // Channel (SMS vs email) is INFERRED from whether `phone_number` or `email`
          // is provided in profile.attributes. No explicit `subscriptions` field.
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'AQ5 cart opt-in',
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttrs,
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
            },
          },
        }),
      });
      // Klaviyo returns 202 Accepted on success for this endpoint (async processing)
      if (!res.ok) {
        // Surface 4xx errors for debugging (logged + GTM event)
        let bodyText = '';
        try { bodyText = await res.text(); } catch (e) {}
        console.error('[AQ5] Klaviyo subscribe HTTP', res.status, bodyText.slice(0, 300));
        gtmPush('aq5_klaviyo_error', { status: res.status });
      }
      return { ok: res.ok, via: 'public-subscribe', status: res.status };
    } catch (err) {
      console.error('[AQ5] Klaviyo subscribe failed', err);
      // AQ5-2 (2026-07-07): fetch rejects with TypeError on network-level
      // failure — in practice almost always an ad-blocker blocking a.klaviyo.com.
      // Distinguish from AbortError (our timeout) so callers can show honest
      // copy instead of a futile "Try again?" loop.
      return {
        ok: false,
        via: 'public-subscribe',
        err: String(err),
        aborted: err && err.name === 'AbortError',
        blocked: err && err.name === 'TypeError',
      };
    }
    };
    // D-160 Part 2: proxy-first (same-origin /apps proxy is ad-blocker-immune),
    // exact-original direct-Klaviyo fallback. Preserves the {ok,via,status} /
    // {ok:false,blocked,aborted} contract the caller switches on.
    // F2 hardening: 6s proxy cap, and a proxy timeout (AbortError) short-circuits
    // with no direct retry — bounds the spinner and avoids a RiHUuf double-write.
    try {
      const pres = await fetchWithTimeout('/apps/ala-capture/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone,
          ...(email ? { email: email } : {}),
          list: KLAVIYO_LIST_ID,
          source: 'AQ5 cart opt-in',
          // No profile properties here: Klaviyo's bulk-subscribe endpoint rejects
          // `properties` (400 -> App Proxy 500). giftChoice is set by the storefront
          // klaviyo identify above; this proxy call carries list + SMS consent only.
        }),
      }, 6000);
      if (pres && (pres.ok || pres.status === 202)) {
        return { ok: true, via: 'proxy', status: pres.status };
      }
    } catch (e) {
      // A proxy timeout (AbortError) is ambiguous — the same-origin write may
      // have reached Klaviyo server-side. Don't double-write (RiHUuf) or double-
      // hang: surface it as aborted and STOP. Only a definitive network failure
      // (TypeError) falls through to the direct call.
      if (e && e.name === 'AbortError') {
        return { ok: false, via: 'proxy', aborted: true, blocked: false };
      }
    }
    return await kdirect();
  }

  // D-046 (iter 2): use fetchJsonWithTimeout so the body-read phase is also
  // covered by the AbortController. /cart/update.js returns the cart as JSON;
  // if we don't read+parse the body, the connection might hang on a chunked
  // response without our timeout firing.
  async function setCartAttrs(attrs) {
    const { res, json } = await fetchJsonWithTimeout('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
    if (!res.ok) throw new Error('cart attr update failed: ' + res.status);
    return json;
  }

  // Q6 (D-027): No app. Add the $0 gift variant directly via Cart Ajax API.
  async function addGiftToCart(giftChoice) {
    const variantMap = window.AQ5_GIFT_VARIANT_IDS || {};
    const variantId = variantMap[giftChoice];
    if (!variantId) {
      console.warn('[AQ5] window.AQ5_GIFT_VARIANT_IDS.' + giftChoice + ' not set — cannot add gift line.');
      return { ok: false, reason: 'unconfigured' };
    }
    // Idempotency: skip if a gift line is already in cart (line item property _aq5_free_gift)
    try {
      const { json: cart } = await fetchJsonWithTimeout('/cart.js', { headers: { 'Accept': 'application/json' } });
      const hasGift = cart && (cart.items || []).some(it =>
        it.properties && (it.properties._aq5_free_gift === 'true' || it.properties._aq5_free_gift === true)
      );
      if (hasGift) return { ok: true, alreadyAdded: true };
    } catch (e) { /* proceed to add */ }
    try {
      const { res, json } = await fetchJsonWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { _aq5_free_gift: 'true' },
          }],
        }),
      });
      if (!res.ok) {
        // body already read as json (or null on parse fail); preserve for logging
        return { ok: false, status: res.status, body: JSON.stringify(json || {}).slice(0, 200) };
      }
      return { ok: true, status: res.status };
    } catch (err) {
      console.error('[AQ5] /cart/add.js failed', err);
      return { ok: false, err: String(err), aborted: err && err.name === 'AbortError' };
    }
  }

  async function refreshCartUI() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) { /* swallow */ }
    if (location.pathname === '/cart') {
      setTimeout(() => location.reload(), 700);
    }
  }

  // ----------- Helpers that operate on a root element (no closure binding) -----------
  // We use DELEGATION instead of per-root listeners because Maestrostack's
  // <m-cart-items> custom element re-renders the entire cart-template section
  // (innerHTML replacement) via Shopify Section Rendering API on cart updates.
  // Per-element listeners would be discarded with the old DOM. Document-level
  // delegation survives any number of DOM replacements.
  // D-028 (2026-05-27): single-step TCPA consent. Submit click = affirmative consent.
  // D-030 (2026-05-27): submit button is NEVER disabled via attribute — only via
  // CSS class for visual feedback. Browsers block clicks on disabled buttons, and
  // if input events get intercepted anywhere in the chain (e.g., cart drawer)
  // the button becomes permanently unclickable. Validate in click handler instead.
  function syncEnabled(root) {
    const phone = root.querySelector('[data-aq5-phone]');
    const giftRadios = root.querySelectorAll('[data-aq5-gift]');
    const submit = root.querySelector('[data-aq5-submit]');
    if (!phone || !submit) return;

    const phoneOk = US_PHONE_RE.test((phone.value || '').trim());
    const giftPicked = Array.from(giftRadios).some(r => r.checked);
    submit.classList.toggle('m-cart__aq5-submit--invalid', !(phoneOk && giftPicked));
  }

  function preselectSingleRadio(root) {
    const giftRadios = root.querySelectorAll('[data-aq5-gift]');
    // Q9 edge case: if only one gift option rendered, pre-select it so the user
    // doesn't have to click. Keeps the form a 2-step (consent + phone), not 3.
    if (giftRadios.length === 1 && !giftRadios[0].checked) {
      giftRadios[0].checked = true;
    }
  }

  // AQ5-2 (2026-07-07): honest note for gift-added-but-subscribe-blocked. When
  // an ad-blocker blocked the Klaviyo subscribe at submit time, the confirmed
  // state still renders (the gift line exists) but the customer was never
  // subscribed — "you'll get a text" would be false without this correction.
  // Injected via JS because we can't edit the Liquid confirmed markup here.
  // Idempotent across re-hydrations ([data-aq5-sms-note] presence check).
  function maybeShowSmsBlockedNote(root) {
    let flagged = false;
    try { flagged = sessionStorage.getItem(SMS_BLOCKED_SS_KEY) === '1'; } catch (e) { /* swallow */ }
    if (!flagged) return;
    const confirmed = root.querySelector('.m-cart__aq5-confirmed');
    if (!confirmed) return;
    if (confirmed.querySelector('[data-aq5-sms-note]')) return;
    const note = document.createElement('span');
    note.setAttribute('data-aq5-sms-note', '');
    note.setAttribute('role', 'status');
    note.style.cssText = 'display:block;margin-top:4px;font-size:11px;color:#9b2c2c;';
    note.textContent = 'Heads up: SMS sign-up didn\'t go through — your browser (or an extension like an ad-blocker) is blocking the sign-up service. Your free gift is still in your cart.';
    confirmed.appendChild(note);
  }

  // ----------- Hydrate (idempotent — safe to call repeatedly after DOM replacements) -----------
  function hydrate(root) {
    // D-053 (2026-05-27): timer can render in EITHER the active form OR the
    // confirmed-state copy ("Free gift added · Reserved for MM:SS"). Start it
    // first, before the active-only early-return below, so the confirmed state
    // also gets the countdown.
    if (root.querySelector('[data-aq5-timer]')) {
      if (root.__aq5TimerInterval) {
        clearInterval(root.__aq5TimerInterval);
        root.__aq5TimerInterval = null;
      }
      startTimer(root);
    }

    // D-029: cart attributes may still say opted-in from a prior session (user
    // cleared cart / removed the gift). Clear those attrs so Klaviyo/analytics
    // don't get false-positive "still opted in" signals. Fire-and-forget; doesn't
    // affect current render (Liquid already decided based on line items). Runs
    // BEFORE the active-only early-return so the locked state also sweeps —
    // otherwise _aq5_optin/_aq5_giftchoice/_aq5_phone leak into order
    // note_attributes after gift removal drops the cart below the threshold.
    // (Safe in every state: the function itself no-ops when a gift line exists.)
    clearStaleAttrsIfNeeded();

    // AQ5-2 (2026-07-07): confirmed state may need the "SMS sign-up didn't go
    // through" note appended (ad-blocker blocked the subscribe at submit time).
    maybeShowSmsBlockedNote(root);

    const active = root.querySelector('[data-aq5-active]');
    if (!active) return; // locked OR confirmed — nothing more to wire up
    preselectSingleRadio(root);
    if (!root.__aq5Shown) {
      const giftRadios = root.querySelectorAll('[data-aq5-gift]');
      gtmPush('aq5_optin_shown', { gift_options_count: giftRadios.length });
      root.__aq5Shown = true;
    }
    syncEnabled(root);
  }

  // D-029: if cart attributes say opted-in but no free-gift line item exists,
  // those attributes are stale (left over from prior session after cart clear).
  // Clear them to keep state clean. Idempotent + safe to call repeatedly.
  let clearStaleInFlight = false;
  async function clearStaleAttrsIfNeeded() {
    if (clearStaleInFlight) return;
    // D-045 (iter 2): suppress while a submit is in flight or just completed
    // (5s grace). Prevents racing against setCartAttrs writing _aq5_optin=true.
    if (window.__aq5_submit_in_flight) return;
    if (window.__aq5_recent_submit_ts && (Date.now() - window.__aq5_recent_submit_ts < 5000)) return;
    clearStaleInFlight = true;
    try {
      const { json: cart } = await fetchJsonWithTimeout('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!cart) return;
      const attrs = cart.attributes || {};
      const optinAttr = attrs._aq5_optin;
      if (optinAttr !== 'true' && optinAttr !== true) return; // not stale
      const hasGiftLine = (cart.items || []).some(it =>
        it.properties && (it.properties._aq5_free_gift === 'true' || it.properties._aq5_free_gift === true)
      );
      if (hasGiftLine) return; // attribute matches line state — not stale
      // Stale: clear the AQ-5 attributes
      await fetchWithTimeout('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          attributes: { _aq5_optin: '', _aq5_giftchoice: '', _aq5_phone: '' },
        }),
      });
    } catch (e) {
      console.warn('[AQ5] clearStaleAttrs failed (non-blocking)', e);
    } finally {
      clearStaleInFlight = false;
    }
  }

  function hydrateAll() {
    document.querySelectorAll('[data-aq5-optin]').forEach(hydrate);
    enforceFreeGiftLimits();
  }

  // ----------- D-052 (2026-05-27): Free-gift quantity guard -----------
  // Prevents customers from incrementing the free-gift line item past qty 1.
  // Two layers:
  //   1. CSS hides the +/- buttons on tagged free-gift lines (aq5-free-gift-line).
  //   2. This function fetches /cart.js after every cart event; any line with
  //      _aq5_free_gift: 'true' at qty > 1 is clamped back to 1 via /cart/change.js.
  // Layer 1 covers ~99% of users (no visible control). Layer 2 is defense-in-depth
  // for anyone who tries direct /cart/change.js or unhides via DevTools.
  let clampInFlight = false;
  async function enforceFreeGiftLimits() {
    if (clampInFlight) return;
    clampInFlight = true;
    try {
      const { json: cart } = await fetchJsonWithTimeout('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!cart || !Array.isArray(cart.items)) return;

      const giftLineIndices = [];
      const keysToClamp = [];
      cart.items.forEach((item, idx) => {
        const isGift = item.properties && (item.properties._aq5_free_gift === 'true' || item.properties._aq5_free_gift === true);
        if (!isGift) return;
        const line = idx + 1; // forloop.index is 1-based; data-index correlates
        giftLineIndices.push(line);
        // Clamp by line-item KEY, not positional index — a positional `line`
        // POSTed from this (possibly stale) snapshot could clamp a PAID line
        // if indices shift between the /cart.js read and the /cart/change.js
        // write. /cart/change.js accepts the key as `id`.
        if (item.quantity > 1) keysToClamp.push(item.key);
      });

      // Tag DOM line items so CSS hides +/- controls. Idempotent: classList.add no-ops if already set.
      giftLineIndices.forEach(line => {
        document.querySelectorAll(`[data-cart-item][data-index="${line}"]`).forEach(el => {
          el.classList.add('aq5-free-gift-line');
        });
      });

      if (keysToClamp.length === 0) return;

      let clampedAny = false;
      for (const key of keysToClamp) {
        try {
          await fetchWithTimeout('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 1 })
          });
          clampedAny = true;
        } catch (e) {
          console.warn('[AQ5] free-gift clamp failed for key', key, e);
        }
      }

      if (clampedAny) {
        // Trigger UI re-render so customer sees qty snap back to 1
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (e) {
      console.warn('[AQ5] enforceFreeGiftLimits failed (non-blocking)', e);
    } finally {
      clampInFlight = false;
    }
  }

  // ----------- Document-level event delegation (one-time setup) -----------
  // capture: true ensures these handlers fire in the CAPTURE phase (top-down)
  // BEFORE any descendant handler can call stopPropagation. Needed because the
  // cart-drawer custom element <m-cart-drawer> has its own click handler that
  // could (in edge cases) absorb events; capture-phase guarantees ours runs first.
  let delegationBound = false;
  function bindDelegation() {
    if (delegationBound) return;
    delegationBound = true;

    function handlePhoneInput(e) {
      const root = e.target.closest && e.target.closest('[data-aq5-optin]');
      if (!root || !e.target.matches('[data-aq5-phone]')) return;
      // 12+-digit inputs, or 11 digits not starting with 1 (US country code),
      // are NOT a US number. Don't silently truncate to 10 digits — that would
      // pass validation and subscribe an undeliverable number. Leave the value
      // untouched (so it fails US_PHONE_RE) and surface the existing error.
      const rawDigits = e.target.value.replace(/\D/g, '');
      if (rawDigits.length > 11 || (rawDigits.length === 11 && rawDigits.charAt(0) !== '1')) {
        setError(root, 'Enter a 10-digit US phone number');
        syncEnabled(root);
        return;
      }
      // Format as user types (only if format changes — avoids cursor jitter on no-op)
      const formatted = formatPhoneAsTyped(e.target.value);
      if (formatted !== e.target.value) {
        e.target.value = formatted;
      }
      const v = formatted.trim();
      if (v && v.replace(/\D/g, '').length > 0 && !US_PHONE_RE.test(v)) {
        setError(root, 'Enter a 10-digit US phone number');
      } else {
        setError(root, '');
      }
      syncEnabled(root);
    }

    // Change events from gift radios (D-028: consent checkbox removed)
    document.addEventListener('change', (e) => {
      const root = e.target.closest && e.target.closest('[data-aq5-optin]');
      if (!root) return;
      if (e.target.matches('[data-aq5-gift], [data-aq5-phone]')) {
        // Also sync on phone change (covers autofill, programmatic value-set)
        if (e.target.matches('[data-aq5-phone]')) handlePhoneInput(e);
        else syncEnabled(root);
      }
    }, true);

    // Phone input — multiple event types as belt-and-suspenders. Some browsers
    // and some Maestrostack interceptors may eat one event type but not all.
    document.addEventListener('input', handlePhoneInput, true);
    document.addEventListener('keyup', handlePhoneInput, true);
    document.addEventListener('paste', (e) => {
      // Run formatter after paste settles into the input value
      setTimeout(() => handlePhoneInput(e), 0);
    }, true);

    // D-049 (iter 2): Enter-key in phone input would otherwise submit the
    // enclosing <form is="m-cart-form"> on /cart, bouncing the user to checkout
    // without ever completing AQ-5. Intercept Enter, prevent default, and
    // synthetically click submit if it's valid.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (!e.target.matches || !e.target.matches('[data-aq5-phone]')) return;
      e.preventDefault();
      const root = e.target.closest('[data-aq5-optin]');
      const submit = root && root.querySelector('[data-aq5-submit]');
      if (submit && !submit.classList.contains('m-cart__aq5-submit--invalid')) {
        submit.click();
      }
    }, true);

    // Click on submit button — runs the full flow.
    // D-030 (2026-05-27): submit is ALWAYS clickable (no `disabled` attribute).
    // We validate phone + gift here on click and show errors instead of blocking.
    // This prevents the "permanently stuck disabled" failure mode that occurs
    // when input events from inside Maestrostack's cart drawer get intercepted
    // before our delegation can re-run syncEnabled.
    // capture: true so we run before <m-cart-drawer>'s click handler (which
    // closes drawer when clicks land outside the inner panel)
    document.addEventListener('click', async (e) => {
      const submit = e.target.closest && e.target.closest('[data-aq5-submit]');
      if (!submit) return;
      const root = submit.closest('[data-aq5-optin]');
      if (!root) return;
      // D-047 (iter 2): always stop propagation for clicks on our submit button —
      // BEFORE the re-entrancy guard. Otherwise a double-click during in-flight
      // submit lets the second click bubble to MCartDrawer's outside-click handler
      // (which would close the drawer mid-submit).
      e.stopPropagation();
      // Re-entrancy guard: only check the in-flight flag, not disabled attr.
      // Also check the window-level flag — a Maestrostack re-render can swap
      // the button element mid-flight, wiping the per-element flag.
      if (submit.__aq5InFlight || window.__aq5_submit_in_flight) return;
      submit.__aq5InFlight = true;
      // D-045 (iter 2): window-level flag so clearStaleAttrsIfNeeded knows to
      // back off while submit is mid-flight (prevents racing against setCartAttrs).
      window.__aq5_submit_in_flight = true;

      const phone = root.querySelector('[data-aq5-phone]');
      const giftRadios = root.querySelectorAll('[data-aq5-gift]');
      const normalized = phone ? normalizePhone(phone.value) : null;
      const giftPicked = Array.from(giftRadios).find(r => r.checked);
      const giftChoice = giftPicked ? giftPicked.value : null;

      if (!normalized) {
        setError(root, 'Enter your 10-digit US phone number above');
        submit.__aq5InFlight = false;
        window.__aq5_submit_in_flight = false;
        return;
      }
      if (!giftChoice) {
        setError(root, 'Pick your free gift first');
        submit.__aq5InFlight = false;
        window.__aq5_submit_in_flight = false;
        return;
      }

      // Inputs valid — proceed with the submit flow
      const originalText = submit.textContent;
      submit.classList.remove('m-cart__aq5-submit--invalid');
      submit.textContent = 'Adding…';
      setError(root, '');

      gtmPush('aq5_optin_submitted', { gift_choice: giftChoice });

      function resetSubmitOnFailure() {
        submit.textContent = originalText;
        submit.__aq5InFlight = false;
        // D-045: also clear the window-level submit-in-flight flag so
        // clearStaleAttrsIfNeeded can resume on the next cart event.
        window.__aq5_submit_in_flight = false;
        syncEnabled(root); // re-apply visual invalid state if appropriate
      }

      try {
        // 1. Add the $0 gift line to cart FIRST (AQ5-4, 2026-07-07 reorder).
        //    Previously the Klaviyo subscribe fired first; if the gift add then
        //    failed (422 OOS etc.) the customer was already subscribed and the
        //    welcome flow's "your free gift is added" was false. Submit is
        //    consent-for-gift: no gift = no subscribe = no message. A failure
        //    here also still lets the user retry without being locked into a
        //    confirmed state.
        const giftRes = await addGiftToCart(giftChoice);
        if (!giftRes.ok && !giftRes.alreadyAdded) {
          console.error('[AQ5] gift add failed', giftRes);
          // Context-aware error copy: only suggest "try the other option" when
          // 2+ gift radios are rendered. With only 1 (smart exclusion fired),
          // there IS no other option to try.
          const oos = giftRes.status === 422;
          const visibleGifts = root.querySelectorAll('[data-aq5-gift]').length;
          let msg;
          if (giftRes.aborted) {
            msg = 'Request timed out — try again.';
          } else if (oos && visibleGifts >= 2) {
            msg = 'That gift is out of stock right now. Try the other option?';
          } else if (oos) {
            msg = 'Sorry, this free gift is temporarily unavailable. Check back soon.';
          } else {
            msg = 'Couldn\'t add the gift. Refresh and try again.';
          }
          setError(root, msg);
          resetSubmitOnFailure();
          return;
        }
        // 2. Subscribe to Klaviyo — only now that the gift is actually in the
        //    cart, so the "gift added" welcome flow can't lie (AQ5-4).
        //    D-040: dedupe by phone — prevents duplicate welcome SMS on retry.
        //    D-048 (iter 2): dedupe key now includes giftChoice so a user who
        //    swaps gift between attempts gets their profile property updated
        //    (lightweight identify — no welcome retrigger).
        let smsBlocked = false;
        const klavKey = normalized + '|' + giftChoice;
        if (window.__aq5_klaviyo_done_key !== klavKey) {
          const isNewPhone = window.__aq5_klaviyo_done_phone !== normalized;
          if (isNewPhone) {
            const klav = await subscribeKlaviyo(normalized, null, giftChoice);
            if (klav.ok) {
              window.__aq5_klaviyo_done_phone = normalized;
              window.__aq5_klaviyo_done_key = klavKey;
              try { sessionStorage.removeItem(SMS_BLOCKED_SS_KEY); } catch (e) { /* swallow */ }
            } else if (klav.blocked) {
              // AQ5-2: network-level TypeError — an ad-blocker (or similar) is
              // blocking a.klaviyo.com. Retrying is futile while the blocker is
              // active, and the gift is already in the cart — NEVER claw it
              // back. Fall through to the confirmed state; hydrate() appends
              // the honest "SMS sign-up didn't go through" note (sessionStorage
              // flag survives the /cart location.reload()). Dedup key stays
              // unset so a genuine future submit re-attempts the subscribe.
              smsBlocked = true;
              try { sessionStorage.setItem(SMS_BLOCKED_SS_KEY, '1'); } catch (e) { /* swallow */ }
              gtmPush('aq5_klaviyo_blocked', { gift_choice: giftChoice });
            } else {
              // Retryable failure (timeout / HTTP error / unconfigured): keep
              // the form active so the user can re-attempt. The gift stays in
              // cart (never claw back) — addGiftToCart is idempotent, so a
              // retry hits the alreadyAdded path and goes straight back here.
              const msg = klav.aborted
                ? 'Request timed out — try again.'
                : 'Couldn\'t reach SMS service. Try again?';
              setError(root, msg);
              resetSubmitOnFailure();
              return;
            }
          } else {
            // Same phone, different gift: update only the profile property via
            // storefront identify (no full subscribe, no welcome retrigger).
            try {
              if (window.klaviyo && typeof window.klaviyo.push === 'function') {
                window.klaviyo.push(['identify', {
                  '$phone_number': normalized,
                  'aq5_giftchoice': giftChoice,
                }]);
              }
            } catch (e) { /* swallow */ }
            window.__aq5_klaviyo_done_key = klavKey;
          }
        }
        // 3. Mark cart as opted-in (locks in the confirmed-state Liquid render)
        await setCartAttrs({
          _aq5_optin: 'true',
          _aq5_giftchoice: giftChoice,
          _aq5_phone: normalized,
        });
        // Offer redeemed — clear urgency state so it doesn't haunt other devices
        clearExpiry();
        // D-045: mark success timestamp so clearStaleAttrsIfNeeded knows to back
        // off for 5s while the cart:updated dispatch from refreshCartUI settles.
        window.__aq5_recent_submit_ts = Date.now();
        window.__aq5_submit_in_flight = false;
        submit.__aq5InFlight = false;
        gtmPush('aq5_gift_added', { gift_choice: giftChoice, sms_blocked: smsBlocked });
        await refreshCartUI();
      } catch (err) {
        console.error('[AQ5] submit failed', err);
        const msg = (err && err.name === 'AbortError')
          ? 'Request timed out — try again.'
          : 'Something went wrong. Refresh and try again.';
        setError(root, msg);
        resetSubmitOnFailure();
      }
    }, true); // capture: true — fire before MCartDrawer's click-outside handler
  }

  // ----------- MutationObserver: re-hydrate on cart-section DOM replacement -----------
  // Watches <m-cart-items> and the cart-drawer container for re-renders. Re-runs
  // hydrate() for any new [data-aq5-optin] blocks that appear.
  // D-032 (2026-05-27): observerBound guard prevents unbounded MutationObserver
  // accumulation on repeat init() calls (which used to happen on every cart:refresh).
  let observerBound = false;
  function bindMutationObserver() {
    if (observerBound) return;
    observerBound = true;
    if (!('MutationObserver' in window)) return;
    const obs = new MutationObserver((mutations) => {
      let needsRehydrate = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && (node.matches('[data-aq5-optin]') || node.querySelector('[data-aq5-optin]'))) {
            needsRehydrate = true;
            break;
          }
        }
        if (needsRehydrate) break;
      }
      if (needsRehydrate) hydrateAll();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // D-032 (2026-05-27): init() runs ONLY ONCE per page (on DOMContentLoaded or
  // immediately). Subsequent cart:refresh/cart:updated events trigger hydrateAll
  // directly (registered below), NOT init() — which used to re-bind everything
  // and leak listeners + observers on every cart update.
  // D-050 (iter 2): Maestrostack pub/sub subscription is now retried up to 5x
  // with 200ms polling. If our defer-script loads in <head> before Maestrostack's
  // body scripts initialize `window.MinimogTheme`, the first attempt silently
  // misses; retry catches the late-arriving globals.
  let maestrostackBound = false;
  function subscribeMaestrostack(attempt) {
    if (maestrostackBound) return;
    attempt = attempt || 0;
    try {
      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        const events = window.MinimogTheme.pubSubEvents;
        if (events.openCartDrawer) window.MinimogEvents.subscribe(events.openCartDrawer, hydrateAll);
        if (events.cartUpdate) window.MinimogEvents.subscribe(events.cartUpdate, hydrateAll);
        maestrostackBound = true;
        return;
      }
    } catch (e) { /* swallow */ }
    if (attempt < 5) setTimeout(() => subscribeMaestrostack(attempt + 1), 200);
  }

  function init() {
    bindDelegation();
    bindMutationObserver();
    hydrateAll();
    subscribeMaestrostack();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-hydrate on cart events — note: hydrateAll (NOT init) so we don't re-bind
  // listeners/observers on every cart update. hydrateAll is idempotent.
  document.addEventListener('cart:refresh', hydrateAll);
  document.addEventListener('cart:updated', hydrateAll);
  // D-051 (iter 2): bfcache restore (Safari/FF back-button navigation) doesn't
  // re-fire DOMContentLoaded but the cart state may have changed in another
  // tab. Re-hydrate to pick up fresh state.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) hydrateAll();
  });
})();
